import SearchIcon from '@mui/icons-material/Search';
import {
  InputAdornment,
  ListSubheader,
  SxProps,
  TextField,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../services/translation/hooks/useTranslation';

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface BaseDropdownProps {
  value: string;
  items: any[];
  onChange: (value: any, event?: SelectChangeEvent) => void;
  label?: string;
  name?: string;
  optionValueGetter?: (item: any) => any;
  optionLabelGetter?: (item: any) => any;
  optionKeyGetter?: (item: any) => any;
  disableCriteria?: (item: any) => any;
  sx?: SxProps;
  forceUseSearch?: boolean;
  disableSearch?: boolean;
  disableNullValue?: boolean;
  disableDefaultEmptyValue?: boolean;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Recursively searches for the specified searchText within text or within the text of an object's properties.
 * Specifically designed to handle nested React elements and their children.
 * @param item - The item to be searched, which could be text, a React element, or an array of such items.
 * @param searchText - The text to search for.
 * @returns `true` if the search text is found, `false` otherwise.
 */
const recursiveSearch = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: string | object | any[],
  searchText: string
): boolean => {
  if (typeof item === 'string') {
    // Direct string comparison
    return item.toLowerCase().includes(searchText.toLowerCase());
  } else if (Array.isArray(item)) {
    // Recurse through each element in the array
    return item.some((child) => recursiveSearch(child, searchText));
  } else if (typeof item === 'object' && item !== null && 'props' in item) {
    // Handle React elements with props
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const props = (item as any).props as { children?: unknown };
    return props.children ? recursiveSearch(props.children, searchText) : false;
  }
  return false;
};

/**
 * Checks if the provided text, or text within an object's properties, contains the specified search text.
 * The object check was done as a workaround so that block ready badge could be searched because the label getter returns a react object instead of a string.  The same is true for the scans search.
 * @param text - The text or object to be searched in. If an object is provided, it will search within the 'children' property.
 * @param searchText - The text to search for.
 * @returns `true` if the search text is found within the text or within the text of the object's properties, `false` otherwise.
 */
const containsText = (text: string | object, searchText: string): boolean => {
  return recursiveSearch(text, searchText);
};

const menuItemStyles: SxProps = {
  fontSize: '1.2rem',
  maxWidth: '100%',
  width: '100%',
};

export const BaseDropdown: React.FC<BaseDropdownProps> = ({
  items,
  value,
  label,
  onChange,
  optionValueGetter,
  optionLabelGetter,
  optionKeyGetter,
  disableCriteria,
  sx,
  name,
  forceUseSearch = false,
  disableSearch = false,
  disableNullValue = false,
  disableDefaultEmptyValue = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState(value);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const searchEnabled = disableSearch
    ? false
    : forceUseSearch || items.length > 5;

  const optionsToDisplay = useMemo(
    () =>
      searchEnabled && searchText
        ? items.filter((option) =>
            containsText(
              optionLabelGetter ? optionLabelGetter(option) : option,
              searchText
            )
          )
        : items,
    [searchEnabled, searchText, items]
  );

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedValue(event.target.value);
    onChange(event.target.value, event);
  };

  // Update selected value if value prop changes (other components may change the value such as when changing scans, the selected feature may update automatically)
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const createMenuItem = (item: any) => {
    let optionValue = item;
    let optionLabel = item;
    let disabled = false;

    if (optionValueGetter) {
      optionValue = optionValueGetter(item);
    }
    if (optionLabelGetter) {
      optionLabel = optionLabelGetter(optionLabel);
    }

    let optionKey = optionLabel;

    if (optionKeyGetter) {
      optionKey = optionKeyGetter(item);
    }

    if (disableCriteria) {
      disabled = disableCriteria(item);
    }

    if (optionValue === value && !optionsToDisplay.includes(item)) {
      disabled = true;
    }

    return (
      <MenuItem
        key={optionKey}
        value={optionValue}
        sx={{ ...menuItemStyles }}
        disabled={disabled}
      >
        {optionLabel}
      </MenuItem>
    );
  };

  const menuItems = useMemo(() => {
    let optionItems = optionsToDisplay.map(createMenuItem);
    // filter out items with empty string values
    optionItems = optionItems.filter((item) => item.props.value !== '');

    if (
      selectedValue &&
      !optionItems.find((item) => item.props.value === selectedValue)
    ) {
      const selectedItem = items.find(
        (item) =>
          (optionValueGetter ? optionValueGetter(item) : item) === selectedValue
      );
      if (selectedItem) {
        optionItems = [createMenuItem(selectedItem), ...optionItems];
      }
    }

    return optionItems;
  }, [optionsToDisplay, selectedValue, items, createMenuItem]);

  const searchInputRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [open]);

  // Compute whether the selectedValue is present in the menu items
  const isValidValue = useMemo(() => {
    return items.some(
      (item) =>
        (optionValueGetter ? optionValueGetter(item) : item) === selectedValue
    );
  }, [selectedValue, items, optionValueGetter]);

  return (
    <FormControl
      sx={{
        width: '100%',
        maxWidth: '100%',
        ...sx,
        border: 'none',
      }}
    >
      <Select
        value={isValidValue ? selectedValue : ''}
        onChange={handleChange}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        MenuProps={{
          autoFocus: false,
          disablePortal: true,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          MenuListProps: {
            subheader: searchEnabled ? (
              <ListSubheader
                sx={{
                  color: theme.colors.common.white,
                  backgroundColor: theme.colors.canopy.background.paper,
                }}
              >
                <TextField
                  color='secondary'
                  ref={searchInputRef}
                  fullWidth
                  size='small'
                  autoFocus
                  variant='standard'
                  placeholder={`${t('Search')}...`}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                  sx={{
                    padding: '8px 0',
                    color: theme.colors.common.white,
                    '& .MuiInputBase-input': {
                      color: theme.colors.common.white,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon
                          sx={{
                            fill: theme.colors.common.white,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </ListSubheader>
            ) : null,
          },
        }}
        displayEmpty
        name={name}
        sx={{
          maxWidth: '100%',
          fontSize: '1.2rem',
          fontWeight: 700,
          color: theme.colors.common.white,
          '& .MuiSelect-select': { p: 0, marginRight: '0.5rem' },
          '& .MuiSelect-icon': {
            fontSize: '2.2rem',
            color: theme.colors.common.white,
            marginLeft: '10px',
          },
          '& .MuiPaper-root': {
            backgroundColor: theme.colors.canopy.background.paper,
            minWidth: '250px',
            whiteSpace: 'nowrap',
            color: theme.colors.common.white,
            '& li': {
              fontSize: '1rem',
            },
            borderRadius: '8px',
            maxHeight: '60%',
          },
          ...sx,
        }}
      >
        {label && !disableNullValue && (
          <MenuItem
            value={''}
            sx={{ ...menuItemStyles }}
            disabled={disableDefaultEmptyValue}
          >
            {label}
          </MenuItem>
        )}
        {menuItems}
      </Select>
    </FormControl>
  );
};

export default BaseDropdown;
